import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import FileUploader from '../../../../../components/FileUpload/FileUploader';
import { childDismissalApi } from './childDismissalApi';
import { dismissalValidation } from './dismissalValidation';

const ChildDismissalModal = ({ 
  show, 
  onHide, 
  child, 
  kgId,
  onSuccess 
}) => {
  const [formData, setFormData] = useState({
    orderNumber: '',
    orderDate: '',
    reason: '',
    dismissalDate: '',
    documentName: '' // Название документа для садика
  });
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [currentStep, setCurrentStep] = useState('form'); // 'form', 'uploading', 'complete'

  // Сброс формы при открытии/закрытии модалки
  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, child]);

  const resetForm = () => {
    setFormData({
      orderNumber: '',
      orderDate: '',
      reason: '',
      dismissalDate: '',
      documentName: ''
    });
    setFiles([]);
    setErrors({});
    setSubmitError('');
    setCurrentStep('form');
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Автоматическое формирование названия документа
    if ((field === 'orderNumber' || field === 'reason') && !formData.documentName) {
      const newDocumentName = `Наказ №${field === 'orderNumber' ? value : formData.orderNumber} - ${field === 'reason' ? value : formData.reason}`;
      setFormData(prev => ({
        ...prev,
        documentName: newDocumentName
      }));
    }
    
    // Очистка ошибки при изменении поля
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileChange = (newFiles) => {
    setFiles(newFiles);
    
    // Очистка ошибки файлов при изменении
    if (errors.files) {
      setErrors(prev => ({
        ...prev,
        files: ''
      }));
    }
  };

  const validateField = (field, value) => {
    let error = '';
    
    switch (field) {
      case 'orderNumber':
        error = dismissalValidation.validateOrderNumber(value);
        break;
      case 'orderDate':
        error = dismissalValidation.validateOrderDate(value);
        break;
      case 'reason':
        error = dismissalValidation.validateReason(value);
        break;
      case 'dismissalDate':
        error = dismissalValidation.validateDismissalDate(value);
        break;
      case 'files':
        error = dismissalValidation.validateFiles(files);
        break;
      default:
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [field]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    
    // Валидация всей формы
    const validation = dismissalValidation.validateForm(formData, files);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setLoading(true);
    setCurrentStep('uploading');

    try {
      // 1. Создаем документ в системе документов садика
      const documentData = {
        documentName: formData.documentName || `Наказ №${formData.orderNumber} - ${formData.reason}`,
        reason: formData.reason
      };

      const createdDocument = await childDismissalApi.createDismissalDocument(kgId, documentData);
      
      if (!createdDocument || !createdDocument._id) {
        throw new Error('Не вдалося створити документ');
      }

      const documentId = createdDocument._id;

      // 2. Загружаем файлы в созданный документ
      const fileUploadPromises = files.map(file => 
        childDismissalApi.uploadDismissalFile(documentId, file)
      );

      await Promise.all(fileUploadPromises);

      // 3. Отправляем данные о выбытии с ссылкой на документ
      const dismissalData = {
        orderNumber: formData.orderNumber.trim(),
        orderDate: formData.orderDate,
        reason: formData.reason.trim(),
        dismissalDate: formData.dismissalDate
      };

      await childDismissalApi.submitDismissalData(kgId, child.id, dismissalData, documentId);

      // Успешное завершение
      setCurrentStep('complete');
      setTimeout(() => {
        onSuccess();
        onHide();
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting dismissal:', error);
      setSubmitError(error.message || 'Помилка при оформленні вибуття. Спробуйте ще раз.');
      setCurrentStep('form');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'uploading':
        return (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" className="mb-3">
              <span className="visually-hidden">Завантаження...</span>
            </Spinner>
            <h5>Обробка даних...</h5>
            <p>Створення документу та завантаження файлів</p>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center py-4">
            <div className="text-success mb-3">
              <i className="bi bi-check-circle-fill" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5>Вибуття успішно оформлено!</h5>
            <p>Документ створено та файли завантажено</p>
          </div>
        );

      default:
        return renderFormContent();
    }
  };

  const renderFormContent = () => {
    if (!child) return null;

    return (
      <>
        {/* Информация о ребенке */}
        <div className="bg-light p-3 rounded mb-4">
          <h6 className="mb-3">Інформація про дитину:</h6>
          <Row>
            <Col md={6}>
              <strong>ПІБ:</strong> {child.lastName} {child.firstName} {child.middleName}
            </Col>
            <Col md={3}>
              <strong>Група:</strong> {child.group}
            </Col>
            <Col md={3}>
              <strong>Дата народження:</strong> {child.birthDate}
            </Col>
          </Row>
        </div>

        {submitError && (
          <Alert variant="danger" className="mb-3">
            {submitError}
          </Alert>
        )}

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Номер наказу <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={formData.orderNumber}
                onChange={(e) => handleInputChange('orderNumber', e.target.value)}
                onBlur={() => validateField('orderNumber', formData.orderNumber)}
                isInvalid={!!errors.orderNumber}
                placeholder="Введіть номер наказа"
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.orderNumber}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>
                Дата наказа <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="date"
                value={formData.orderDate}
                onChange={(e) => handleInputChange('orderDate', e.target.value)}
                onBlur={() => validateField('orderDate', formData.orderDate)}
                isInvalid={!!errors.orderDate}
                max={getCurrentDate()}
                disabled={loading}
              />
              <Form.Control.Feedback type="invalid">
                {errors.orderDate}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>
            Причина вибуття <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={formData.reason}
            onChange={(e) => handleInputChange('reason', e.target.value)}
            onBlur={() => validateField('reason', formData.reason)}
            isInvalid={!!errors.reason}
            placeholder="Вкажіть причину вибуття дитини з садка"
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            {errors.reason}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>
            Назва документа <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="text"
            value={formData.documentName}
            onChange={(e) => handleInputChange('documentName', e.target.value)}
            placeholder="Назва документа для системи"
            disabled={loading}
          />
          <Form.Text className="text-muted">
            Автоматично формується з номера наказа та причини вибуття
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-4">
          <Form.Label>
            Дата вибуття <span className="text-danger">*</span>
          </Form.Label>
          <Form.Control
            type="date"
            value={formData.dismissalDate}
            onChange={(e) => handleInputChange('dismissalDate', e.target.value)}
            onBlur={() => validateField('dismissalDate', formData.dismissalDate)}
            isInvalid={!!errors.dismissalDate}
            max={getCurrentDate()}
            disabled={loading}
          />
          <Form.Control.Feedback type="invalid">
            {errors.dismissalDate}
          </Form.Control.Feedback>
        </Form.Group>

        {/* Компонент загрузки файлов */}
        <FileUploader
          label="Скан-копія наказа на вибуття"
          description="Завантажте скан-копію наказа у форматі PDF, JPG, PNG. Максимальний розмір файлу 2 МБ."
          value={files}
          onChange={handleFileChange}
          required={true}
          disabled={loading}
          multiple={false}
          errorVal={errors.files}
        />
      </>
    );
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>Оформлення вибуття дитини</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {renderStepContent()}
        </Modal.Body>

        {currentStep === 'form' && (
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={onHide}
              disabled={loading}
            >
              Скасувати
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Обробка...
                </>
              ) : (
                'Оформити вибуття'
              )}
            </Button>
          </Modal.Footer>
        )}

        {currentStep === 'complete' && (
          <Modal.Footer>
            <Button variant="success" disabled>
              Успішно завершено
            </Button>
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default ChildDismissalModal;